// Monoline Quarkle icon set — distilled from components_v2/icons.jsx in the
// Editor.html handoff. All icons accept { size, color, strokeWidth, className, style }.

import React from "react";

function Ico({ children, size = 20, color = "currentColor", sw = 1.6, style, className, viewBox = "0 0 24 24" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ display: "block", ...(style || {}) }}
    >
      {children}
    </svg>
  );
}

export function IcoPlanet({ size = 20, color = "currentColor", style, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="70 98 138 138"
      fill="none"
      stroke={color}
      strokeWidth="3"
      className={className}
      style={{ display: "block", ...(style || {}) }}
    >
      <circle cx="139" cy="167" r="68" />
      <path d="M72 148 Q139 158 206 148" />
      <path d="M72 168 Q139 178 206 168" />
      <path d="M78 188 Q139 198 200 188" />
      <path d="M90 208 Q139 216 188 208" />
    </svg>
  );
}

/* Landing page logomark — three orbs. Matches Landing v6. */
export function QuarkleLogo({ size = 34, color = "currentColor", style, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      className={className}
      style={{ display: "block", color, ...(style || {}) }}
      aria-label="Quarkle"
      role="img"
    >
      <circle cx="62" cy="18" r="10" stroke="currentColor" strokeWidth="3" />
      <circle cx="20" cy="24" r="14" stroke="currentColor" strokeWidth="3" />
      <line x1="10" y1="22" x2="30" y2="22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="8" y1="27" x2="24" y2="27" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="46" cy="58" r="22" stroke="currentColor" strokeWidth="3" />
      <path d="M27 52 Q37 47 47 52 T67 54" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M25 62 Q37 57 47 62 T69 62" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M28 70 Q38 66 48 70" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/* Landing page wordmark — Gambarino with italic "uar" */
export function QuarkleWordmark({ size = 22, color = "var(--qk-ink)", style, className }) {
  return (
    <span
      className={className}
      style={{
        fontFamily: "'Gambarino', 'DM Serif Display', serif",
        fontSize: size,
        color,
        lineHeight: 0.9,
        letterSpacing: "-0.04em",
        fontWeight: 400,
        display: "inline-block",
        ...(style || {}),
      }}
    >
      Q<span style={{ fontStyle: "italic" }}>uar</span>kle
    </span>
  );
}

/* Bespoke: speech-bubble with strokes — "comments" */
export const IcoComments = (p) => (
  <Ico {...p}>
    <path d="M4 5h16v11H10l-5 4v-4H4z" />
    <path d="M8 9h8M8 12h5" />
  </Ico>
);

/* Bespoke: open book with eye — "overview" */
export const IcoOverview = (p) => (
  <Ico {...p}>
    <path d="M3 5h6c1.5 0 3 .8 3 2.5V20c-.5-1-1.5-2-3-2H3z" />
    <path d="M21 5h-6c-1.5 0-3 .8-3 2.5V20c.5-1 1.5-2 3-2h6z" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </Ico>
);

/* Bespoke: a framed picture with a horizon and a sun — the drawing room */
export const IcoArtTab = (p) => (
  <Ico {...p}>
    <rect x="3" y="4" width="18" height="16" rx="1.5" />
    <path d="M3 15l4.5-4 3.5 3 4-5 5 6" />
    <circle cx="8.5" cy="8.5" r="1.5" />
  </Ico>
);

/* Bespoke: sparkle + spark */
export const IcoSpark = (p) => (
  <Ico {...p}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6" />
  </Ico>
);

/* Bespoke: tuning knob — "customize" */
export const IcoTune = (p) => (
  <Ico {...p}>
    <path d="M4 6h10M18 6h2" />
    <path d="M4 12h4M12 12h8" />
    <path d="M4 18h12M20 18h0" />
    <circle cx="16" cy="6" r="2" fill="currentColor" fillOpacity="0.15" />
    <circle cx="10" cy="12" r="2" fill="currentColor" fillOpacity="0.15" />
    <circle cx="18" cy="18" r="2" fill="currentColor" fillOpacity="0.15" />
  </Ico>
);

/* Bespoke: image/attach for composer */
export const IcoAttach = (p) => (
  <Ico {...p}>
    <path d="M17 8l-7 7a3 3 0 104 4l7-7a5 5 0 00-7-7l-7 7a7 7 0 0010 10" />
  </Ico>
);

/* Bespoke: tiny sparkle mark (pink accent) */
export const IcoMiniSparkle = ({ size = 12, color = "currentColor", style, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} className={className} style={{ display: "block", ...(style || {}) }}>
    <path d="M13 2c.5 4.5 2 6 6.5 6.5-4.5.5-6 2-6.5 6.5-.5-4.5-2-6-6.5-6.5 4.5-.5 6-2 6.5-6.5z" />
  </svg>
);

/* Companion tab icons — refined, brand-aligned.
   All three share a 1.6-stroke monoline vocabulary to match the icon
   system, with small ornaments (a planet dot, an ink mark, a leaf) that
   tie back to the Quarkle identity. */

/* CHAT — speech scroll with a tiny planet in the corner */
export const IcoChatTab = (p) => (
  <Ico {...p}>
    <path d="M4 5h14a2 2 0 012 2v7a2 2 0 01-2 2h-7l-4.5 3.5V16H4a1 1 0 01-1-1V6a1 1 0 011-1z" />
    <circle cx="9" cy="10.5" r="0.7" fill="currentColor" />
    <circle cx="12.5" cy="10.5" r="0.7" fill="currentColor" />
    <circle cx="16" cy="10.5" r="0.7" fill="currentColor" />
    <circle cx="20.5" cy="4.5" r="1.8" />
  </Ico>
);

/* REVIEW — loupe over a page of lines. Reads as "close reading / critique"
   without competing with the editor's IcoLoupe (which is a bare magnifier). */
export const IcoReviewTab = (p) => (
  <Ico {...p}>
    {/* page + fold */}
    <path d="M5 3h8l4 4v5" />
    <path d="M13 3v4h4" />
    {/* text lines */}
    <path d="M7 9h5M7 12h4" strokeWidth="1.2" opacity="0.75" />
    {/* loupe overlapping the page corner */}
    <circle cx="13" cy="16" r="4" />
    <path d="M16 19l4 4" />
  </Ico>
);

/* KNOWLEDGE — open atlas with a page-fold leaf */
export const IcoKnowledgeTab = (p) => (
  <Ico {...p}>
    <path d="M3 5h7c1.1 0 2 .6 2 2v13c-.3-1-1-1.5-2-1.5H3z" />
    <path d="M21 5h-7c-1.1 0-2 .6-2 2v13c.3-1 1-1.5 2-1.5h7z" />
    <path d="M5.5 9h3.5M5.5 12h3.5M15 9h3.5M15 12h3.5" />
    <path d="M12 7l1.5-2 2 1" strokeWidth="1.2" opacity="0.6" />
  </Ico>
);

/* SEARCH — a loupe with prose inside the lens: finding passages, not files.
   Distinct from IcoReviewTab (loupe over a page corner) and IcoLoupe (bare). */
export const IcoSearchTab = (p) => (
  <Ico {...p}>
    <circle cx="10.5" cy="10.5" r="6.2" />
    <path d="M8 9.3h5M8 11.8h3.5" strokeWidth="1.2" opacity="0.75" />
    <path d="M15.2 15.2L21 21" />
  </Ico>
);

export const IcoLibrary = (p) => (
  <Ico {...p}>
    <rect x="4" y="4" width="4.5" height="16" rx="0.6" />
    <rect x="10" y="6" width="4.5" height="14" rx="0.6" />
    <path d="M16.5 7.2l3.2-.9 2 13.2-3.2.9z" />
  </Ico>
);

export const IcoImport = (p) => (
  <Ico {...p}>
    <path d="M12 3v10" />
    <path d="M8 9l4 4 4-4" />
    <path d="M4 15v4a1 1 0 001 1h14a1 1 0 001-1v-4" />
  </Ico>
);

export const IcoVersions = (p) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="7" />
    <path d="M12 8v4l2.5 1.5" />
    <path d="M5 5l1.5 2M19 5l-1.5 2" />
  </Ico>
);

export const IcoExport = (p) => (
  <Ico {...p}>
    <path d="M6 3h8l4 4v14H6z" />
    <path d="M14 3v4h4" />
    <path d="M9 14h7M13 11l3 3-3 3" />
  </Ico>
);

export const IcoPlus = (p) => (
  <Ico {...p}>
    <path d="M12 5v14M5 12h14" />
  </Ico>
);

export const IcoShare = (p) => (
  <Ico {...p}>
    <circle cx="6" cy="12" r="2.2" />
    <circle cx="18" cy="6" r="2.2" />
    <circle cx="18" cy="18" r="2.2" />
    <path d="M8 11l8-4M8 13l8 4" />
  </Ico>
);

export const IcoCrown = (p) => (
  <Ico {...p}>
    <path d="M3 8l3 8h12l3-8-5 3-4-6-4 6z" />
    <path d="M6 20h12" />
  </Ico>
);

export const IcoCompass = (p) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5z" fill="currentColor" fillOpacity="0.12" />
  </Ico>
);

export const IcoSparkle = ({ size = 20, color = "currentColor", style, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    style={{ display: "block", ...(style || {}) }}
  >
    <path d="M13 2c.5 4.5 2 6 6.5 6.5-4.5.5-6 2-6.5 6.5-.5-4.5-2-6-6.5-6.5 4.5-.5 6-2 6.5-6.5z" />
    <path d="M6 14c.3 2.3 1 3 3.3 3.3-2.3.3-3 1-3.3 3.3-.3-2.3-1-3-3.3-3.3 2.3-.3 3-1 3.3-3.3z" />
  </svg>
);

export const IcoMuse = (p) => (
  <Ico {...p}>
    <path d="M12 3c3 3 3 7 0 10-3-3-3-7 0-10z" />
    <circle cx="12" cy="17" r="4" />
    <path d="M8 21h8" />
  </Ico>
);

export const IcoScribe = (p) => (
  <Ico {...p}>
    <path d="M4 20l3-1 12-12-2-2L5 17z" />
    <path d="M14 6l4 4" />
    <path d="M4 20h7" />
  </Ico>
);

export const IcoAtlas = (p) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c3 3 4 6 4 9s-1 6-4 9" />
    <path d="M12 3c-3 3-4 6-4 9s1 6 4 9" />
  </Ico>
);

// Four strokes and the fifth across: the tally marks the Stats tab counts in.
export const IcoTally = (p) => (
  <Ico {...p}>
    <path d="M6 6v12M9.5 6v12M13 6v12M16.5 6v12" />
    <path d="M4 16.5L19 8.5" />
  </Ico>
);

export const IcoLoupe = (p) => (
  <Ico {...p}>
    <circle cx="10" cy="10" r="6" />
    <path d="M14.5 14.5L20 20" />
    <path d="M7 10h6" />
  </Ico>
);

export const IcoThread = (p) => (
  <Ico {...p}>
    <circle cx="6" cy="6" r="2" />
    <path d="M6 8v6a4 4 0 004 4h8" />
    <path d="M16 14l4 4-4 4" />
  </Ico>
);

export const IcoLeaf = (p) => (
  <Ico {...p}>
    <path d="M4 20c0-8 6-14 16-16-1 10-7 16-16 16z" />
    <path d="M4 20l10-10" />
  </Ico>
);

export const IcoBook = (p) => (
  <Ico {...p}>
    <path d="M3 5h6c1.5 0 3 .8 3 2.5V20c-.5-1-1.5-2-3-2H3z" />
    <path d="M21 5h-6c-1.5 0-3 .8-3 2.5V20c.5-1 1.5-2 3-2h6z" />
  </Ico>
);

export const IcoSend = (p) => (
  <Ico {...p}>
    <path d="M3 12l18-8-6 18-4-8-8-2z" />
  </Ico>
);

export const IcoAnchor = (p) => (
  <Ico {...p}>
    <path d="M6 4h12v16l-6-4-6 4z" />
  </Ico>
);

export const IcoTarget = (p) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </Ico>
);

export default Ico;
